customElements.whenDefined('search-form').then(() => {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.productTypes = this.querySelector('.js-product-types');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', debounce((event) => { this.onChange(event);}, 300).bind(this));
      this.productTypes?.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.closest('.template-search__search') || this.closest('.header__search-bar')) {
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      this.addEventListener('click', (event) => {
        if (event.target.matches('.inline-search__close-button') || event.target.closest('.inline-search__close-button')) {
          this.onFormReset();
          this.close();
          document.activeElement.blur();
          setTimeout(() => { document.activeElement.blur(); }, 500);
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    getPartNumberIndexUrl() {
      const script = document.querySelector('script[src*="predictive-search.js"]');
      if (!script?.src) return '/cdn/shop/t/assets/part-number-index.json';
      return script.src.replace(/predictive-search(?:\.[^/?]+)?\.js(\?.*)?$/, 'part-number-index.json$1');
    }

    isPartNumberQuery(query) {
      return /^(?:[A-Za-z]+[-\s]*)?\d[A-Za-z0-9-]*$/.test(query);
    }

    normalizePartNumber(value) {
      return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    removePartNumberPrefix(value) {
      return value.replace(/^[A-Za-z]+[-\s]+/, '');
    }

    getProductPartNumber(product) {
      return product.title.split('|')[0].trim();
    }

    isExactPartNumberProduct(query, product) {
      const normalizedQuery = this.normalizePartNumber(query);
      const partNumber = this.getProductPartNumber(product);
      const normalizedPartNumber = this.normalizePartNumber(partNumber);
      const normalizedHandle = this.normalizePartNumber(product.handle || '');
      const normalizedPartNumberWithoutPrefix = this.normalizePartNumber(this.removePartNumberPrefix(partNumber));

      return normalizedQuery === normalizedPartNumber ||
        normalizedQuery === normalizedHandle ||
        normalizedQuery === normalizedPartNumberWithoutPrefix;
    }

    getExactPartNumberSearchQuery(query) {
      return /^[A-Za-z]+[-\s]+/.test(query) ? `"${query}"` : query;
    }

    formatMoney(amount, currency) {
      const numeric = Number(amount);
      if (!amount || Number.isNaN(numeric)) return '';

      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency || 'USD'
        }).format(numeric);
      } catch (error) {
        return `$${numeric.toFixed(2)}`;
      }
    }

    escapeHtml(value) {
      return String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[char]);
    }

    partIndexRecordToProduct(record) {
      const partNumber = record[0] || '';
      return {
        partNumber,
        normalized: this.normalizePartNumber(partNumber),
        stripped: this.normalizePartNumber(this.removePartNumberPrefix(partNumber)),
        handle: record[1] || '',
        title: record[2] || partNumber,
        vendor: record[3] || '',
        productType: record[4] || '',
        image: record[5] || '',
        imageAlt: record[6] || record[2] || partNumber,
        price: record[7] || '',
        currency: record[8] || ''
      };
    }

    scorePartNumberProduct(product, normalizedQuery) {
      const values = [product.normalized, product.stripped].filter(Boolean);
      if (values.some((value) => value === normalizedQuery)) return 0;
      if (values.some((value) => value.startsWith(normalizedQuery))) return 10;
      if (values.some((value) => value.includes(normalizedQuery))) return 20;
      return 999;
    }

    async getPartNumberIndexMatches(query) {
      if (!this.isPartNumberQuery(query)) return [];

      const normalizedQuery = this.normalizePartNumber(query);
      if (normalizedQuery.length < 4) return [];

      const response = await fetch(this.getPartNumberIndexUrl());
      if (!response.ok) return [];

      const index = await response.json();
      return (index.products || [])
        .map((record) => this.partIndexRecordToProduct(record))
        .map((product) => ({ product, score: this.scorePartNumberProduct(product, normalizedQuery) }))
        .filter((item) => item.score < 999)
        .sort((a, b) => a.score - b.score || a.product.partNumber.length - b.product.partNumber.length || a.product.partNumber.localeCompare(b.product.partNumber, undefined, { numeric: true }))
        .map((item) => item.product);
    }

    renderPartNumberPredictiveResults(query, matches) {
      const limitedMatches = matches.slice(0, 6);
      const resultText = `Found ${matches.length} ${matches.length === 1 ? 'result' : 'results'} for &ldquo;${this.escapeHtml(query)}&rdquo;`;

      const productsMarkup = limitedMatches.map((product, index) => {
        const price = this.formatMoney(product.price, product.currency);
        const productUrl = `/products/${encodeURIComponent(product.handle)}`;

        return `
          <li
            id="predictive-search-option-part-number-${index + 1}"
            class="predictive-search__list-item"
            role="option"
            aria-selected="false"
          >
            <a
              href="${productUrl}"
              class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text"
              tabindex="-1"
            >
              ${product.image
                ? `<img class="predictive-search__image" src="${this.escapeHtml(product.image)}" alt="${this.escapeHtml(product.imageAlt)}" width="50" height="50" loading="lazy" decoding="async">`
                : ''}
              <div class="predictive-search__item-content">
                ${product.vendor ? `<div class="predictive-search__item-vendor t6">${this.escapeHtml(product.vendor)}</div>` : ''}
                <p class="predictive-search__item-heading t3">${this.escapeHtml(product.title)}</p>
                ${price ? `<div class="price">${this.escapeHtml(price)}</div>` : ''}
              </div>
            </a>
          </li>
        `;
      }).join('');

      this.renderSearchResults(`
        <div id="predictive-search-results" role="listbox">
          <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
            <div class="predictive-search__result-group">
              <div>
                <h2 id="predictive-search-products" class="predictive-search__heading text-body t3">
                  ${resultText}
                </h2>
                <ul
                  id="predictive-search-results-products-list"
                  class="predictive-search__results-list list-unstyled"
                  role="group"
                  aria-labelledby="predictive-search-products"
                >
                  ${productsMarkup}
                </ul>
                ${matches.length > limitedMatches.length ? `<p class="predictive-search__heading text-body t3">Press Enter to see all ${matches.length} matches.</p>` : ''}
              </div>
            </div>
          </div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>Found ${matches.length} ${matches.length === 1 ? 'result' : 'results'}</span>
      `);
    }

    async getPartNumberSearchResult(query) {
      const formData = new FormData(document.querySelector('.predictive_search_form') || document.querySelector('.predictive_search_page_form'));
      const params = new URLSearchParams(formData);
      params.set('q', query);
      params.set('resources[type]', 'product');

      const response = await fetch(`${routes.predictive_search_url}.json?${params.toString()}`);
      if (!response.ok) return { exactProduct: null, hasProducts: false };

      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      return {
        exactProduct: products.find((product) => this.isExactPartNumberProduct(query, product)),
        hasProducts: products.length > 0
      };
    }

    syncSearchInputs(query) {
      document.querySelectorAll('input[type="search"][name="q"]').forEach((input) => {
        input.value = query;
      });
    }

    onChange() {
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.classList.add('opacity-20');
      }

      if(newSearchTerm.length === 0){
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    async onFormSubmit(event) {
      const query = this.getQuery();
      if (!query.length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
        return;
      }

      if (!this.isPartNumberQuery(query)) return;

      event.preventDefault();

      const searchResult = await this.getPartNumberSearchResult(query).catch(() => ({ exactProduct: null, hasProducts: false }));
      const exactProduct = searchResult.exactProduct;
      if (exactProduct?.url) {
        window.location.href = exactProduct.url;
        return;
      }

      const exactSearchQuery = searchResult.hasProducts ? query : this.getExactPartNumberSearchQuery(query);
      if (exactSearchQuery !== query) this.syncSearchInputs(exactSearchQuery);
      this.input.form.submit();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (window.visualViewport) {
        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 100);

        setTimeout(() => {
          document.documentElement.style.setProperty('--bvh', `${window.visualViewport.height}px`);
        }, 500);
      }

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
          (element) => element.offsetParent !== null
      );
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      const formData = new FormData(document.querySelector('.predictive_search_form') || document.querySelector('.predictive_search_page_form'));
      const params = new URLSearchParams(formData);

      if (this.productTypes && this.productTypes.value.length > 0) {
        searchTerm = `product_type:${this.productTypes.value} AND ${searchTerm}`;
      }

      params.set('q', searchTerm);

      const urlParams = params.toString();
      const queryKey = urlParams.toLowerCase();

      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        this.renderPartNumberResultsIfBetter(this.getQuery());
        return;
      }

      fetch(`${routes.predictive_search_url}?${urlParams}`, {
        signal: this.abortController.signal,
      })
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              this.close();
              throw error;
            }

            return response.text();
          })
          .then((text) => {
            const resultsMarkup = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('#shopify-section-predictive-search').innerHTML;
            // Save bandwidth keeping the cache in all instances synced
            this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            });
            this.renderSearchResults(resultsMarkup);
            this.renderPartNumberResultsIfBetter(this.getQuery());
          })
          .catch((error) => {
            if (error?.code === 20) {
              // Code 20 means the call was aborted
              return;
            }
            this.close();
            throw error;
          });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.querySelector('.search-suggestions')?.classList.add('hidden');

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
      if (window.updateViewportSize) window.updateViewportSize();
    }

    async renderPartNumberResultsIfBetter(query) {
      if (!this.isPartNumberQuery(query)) return;

      const matches = await this.getPartNumberIndexMatches(query).catch(() => []);
      if (!matches.length || query !== this.getQuery()) return;

      const nativeProductCount = this.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item').length;
      if (nativeProductCount > 0 && matches.length <= nativeProductCount) return;

      this.renderPartNumberPredictiveResults(query, matches);
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
      }
      this.querySelector('.search-suggestions')?.classList.remove('hidden');
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', false);
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
});
